"use client";

import { config } from "@/config/env";

export const STRIPE_PRICES = {
  small: config.smallPriceId,
  medium: config.mediumPriceId,
  large: config.largePriceId,
} as const;

export type PriceTier = keyof typeof STRIPE_PRICES;

interface CheckoutResponse {
  checkoutUrl: string;
}

interface PortalResponse {
  portalUrl: string;
}

/**
 * Create an authenticated fetcher for Stripe API calls
 */
async function authFetch(
  endpoint: string,
  getToken: () => Promise<string | null>,
  options: RequestInit = {}
): Promise<Response> {
  const token = await getToken();

  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(`${config.apiUrl}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Request failed" }));
    throw new Error(error.message || `Request failed: ${response.status}`);
  }

  return response;
}

/**
 * Create a Stripe Checkout session and return the checkout URL
 */
export async function createCheckoutSession(
  priceId: string,
  getToken: () => Promise<string | null>,
  options?: {
    successUrl?: string;
    cancelUrl?: string;
  }
): Promise<string> {
  const response = await authFetch(
    "/api/stripe/checkout",
    getToken,
    {
      method: "POST",
      body: JSON.stringify({
        priceId,
        successUrl: options?.successUrl,
        cancelUrl: options?.cancelUrl,
      }),
    }
  );

  const data: CheckoutResponse = await response.json();
  return data.checkoutUrl;
}

/**
 * Create a Stripe Customer Portal session and return the portal URL
 */
export async function createPortalSession(
  getToken: () => Promise<string | null>
): Promise<string> {
  const response = await authFetch(
    "/api/stripe/portal",
    getToken,
    { method: "POST" }
  );

  const data: PortalResponse = await response.json();
  return data.portalUrl;
}

/**
 * Redirect to Stripe Checkout for a given price tier
 */
export async function redirectToCheckout(
  tier: PriceTier,
  getToken: () => Promise<string | null>,
  options?: {
    successUrl?: string;
    cancelUrl?: string;
  }
): Promise<void> {
  const priceId = STRIPE_PRICES[tier];
  if(!priceId) return;
  
  const checkoutUrl = await createCheckoutSession(priceId, getToken, options);
  window.location.href = checkoutUrl;
}

/**
 * Redirect to Stripe Customer Portal
 */
export async function redirectToPortal(
  getToken: () => Promise<string | null>
): Promise<void> {
  const portalUrl = await createPortalSession(getToken);
  window.location.href = portalUrl;
}
